// rtuWebGatewaySessions.ts - which RTU web-app sessions were opened from THIS DMS tab,
// and the best-effort "close everything" when the signed-in account ends or changes.
//
// Leaf-ish module (imports only config + sessionIdentity) so authService and keycloak.ts
// can call it without an import cycle. The keep-alive timer and the API calls live in
// rtuWebGatewayService.ts.

import { API_ENDPOINTS, getBaseUrl } from '../config/apiConfig';
import { SessionEndReason, subscribeSessionEnding } from './sessionIdentity';

export const CLOSE_ALL_TIMEOUT_MS = 3000;

type Listener = () => void;

// Session ids and the RTU each belongs to (never tokens or launch URLs) survive a reload of this DMS tab, so its keep-alive
// resumes and an open RTU web tab is not closed just because DMS was reloaded. sessionStorage is per tab and cleared when
// the tab closes. Entries stored by an older build (plain id strings, no rtuId) are ignored.
const STORAGE_KEY = 'dms.rtuWebGateway.sessionIds';
const SESSION_ID_PATTERN = /^[0-9a-f-]{36}$/;

const isRtuId = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value > 0;

const restoreSessions = (): Array<[string, number]> => {
  try {
    const raw = typeof sessionStorage === 'undefined' ? null : sessionStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    const entries: Array<[string, number]> = [];
    parsed.forEach((entry: unknown) => {
      const { sessionId, rtuId } = (entry && typeof entry === 'object' ? entry : {}) as { sessionId?: unknown; rtuId?: unknown };
      if (typeof sessionId === 'string' && SESSION_ID_PATTERN.test(sessionId) && isRtuId(rtuId)) {
        entries.push([sessionId, rtuId]);
      }
    });
    return entries.slice(0, 20);
  } catch {
    return [];
  }
};

// sessionId -> rtuId (null only for a session tracked without knowing its RTU; such a session is not persisted).
const trackedSessions = new Map<string, number | null>(restoreSessions());

const persist = () => {
  try {
    if (typeof sessionStorage === 'undefined') return;
    const stored = Array.from(trackedSessions.entries())
      .filter((entry): entry is [string, number] => entry[1] !== null)
      .map(([sessionId, rtuId]) => ({ sessionId, rtuId }));
    if (stored.length === 0) {
      sessionStorage.removeItem(STORAGE_KEY);
    } else {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    }
  } catch {
    // storage unavailable: keep-alive simply does not survive a reload
  }
};
const listeners = new Set<Listener>();
// Bearer token of the account that owns the tracked sessions, refreshed whenever a
// session is opened or kept alive. On an account change the live token is already the
// NEW account's, so close-all must use this snapshot instead.
let ownerToken: string | null = null;

const notify = () => {
  persist();
  listeners.forEach((listener) => listener());
};

export const trackRtuWebSession = (sessionId: string, token?: string | null, rtuId?: number | null): void => {
  if (token) {
    ownerToken = token;
  }
  const knownRtuId = isRtuId(rtuId) ? rtuId : null;
  if (!trackedSessions.has(sessionId) || (knownRtuId !== null && trackedSessions.get(sessionId) !== knownRtuId)) {
    trackedSessions.set(sessionId, knownRtuId);
    notify();
  }
};

export const untrackRtuWebSessions = (sessionIds: string[]): void => {
  let changed = false;
  sessionIds.forEach((id) => {
    changed = trackedSessions.delete(id) || changed;
  });
  if (trackedSessions.size === 0) {
    ownerToken = null;
  }
  if (changed) {
    notify();
  }
};

export const getTrackedRtuWebSessionIds = (): string[] => Array.from(trackedSessions.keys());

/** The tracked RTU web session opened from this tab for the given RTU, if any (the most recent one). */
export const getTrackedRtuWebSessionForRtu = (rtuId: number): string | null => {
  let found: string | null = null;
  trackedSessions.forEach((trackedRtuId, sessionId) => {
    if (trackedRtuId === rtuId) found = sessionId;
  });
  return found;
};

export const refreshRtuWebOwnerToken = (token: string | null | undefined): void => {
  if (token && trackedSessions.size > 0) {
    ownerToken = token;
  }
};

export const subscribeTrackedRtuWebSessions = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Closes every RTU web session opened from this tab, using the OUTGOING account's token
 * (`token` when the caller still has it, otherwise the snapshot), then forgets the ids.
 * Fire-and-forget with a short timeout; `keepalive` lets it survive a logout redirect.
 * Never throws and never blocks the caller.
 */
export const closeAllRtuWebSessions = (reason: SessionEndReason, token?: string | null): void => {
  if (trackedSessions.size === 0) {
    return;
  }
  const bearer = token || ownerToken;
  trackedSessions.clear();
  ownerToken = null;
  notify();
  if (!bearer || typeof fetch !== 'function') {
    return;
  }
  try {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), CLOSE_ALL_TIMEOUT_MS) : null;
    const done = () => {
      if (timer) clearTimeout(timer);
    };
    const request = fetch(`${getBaseUrl()}${API_ENDPOINTS.RTU_WEB_GATEWAY.CLOSE_ALL}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${bearer}` },
      body: JSON.stringify({ reason }),
      keepalive: true,
      signal: controller?.signal,
    });
    Promise.resolve(request).then(done, done);
  } catch {
    // best effort only
  }
};

// Account change / sign-out as seen by the session-identity hook (runs before user
// state is reset). Direct sign-out paths call closeAllRtuWebSessions first with the
// live token; this is then a no-op.
subscribeSessionEnding((reason) => closeAllRtuWebSessions(reason));
